import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { useGoogleMapsLoader } from "hooks/googleMapsLoader";
import { mnt } from "theme/maintenanceTokens";

/**
 * N5 — Plan locations map. Adapted from `components/ServiceAreaMap` (same
 * loader, same imperative overlay + InfoWindow pattern), but plots POINT
 * markers for a plan's locations instead of polygons, colored by the
 * location's work-order status.
 *
 * Status buckets + colors (mnt palette, so a marker reads the same as the
 * roster status pills):
 *   no_wo         grey / hollow      nothing raised, never serviced
 *   open          amber              WO raised, pre-production
 *   in_production teal               project in progress
 *   closed        green              has completed service history
 * `overdue` draws a red ring around whatever the base color is (a second
 * axis, not a bucket).
 *
 * The pin click opens a summary popover: the location's details plus a
 * short read of what we have there (status, open WO count) with links to
 * open the location.
 */

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }; // center of US

export type PlanMapStatus = "no_wo" | "open" | "in_production" | "closed";

export interface PlanMapPoint {
  locationId: string | null;
  name?: string | null;
  address?: any;
  latitude: number | null;
  longitude: number | null;
  batchId?: string | null;
  batchName?: string | null;
  mapStatus: PlanMapStatus;
  overdue?: boolean;
  openWoCount?: number;
  /** Newest actionable WO at this site, for the popover's "View work order". */
  activeWorkOrderId?: string | null;
}

interface PlanLocationsMapProps {
  points: PlanMapPoint[];
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  /** Open the location detail page (navigation handled by the parent). */
  onOpenLocation?: (locationId: string) => void;
  /** Open a work order detail page (navigation handled by the parent). */
  onOpenWorkOrder?: (workOrderId: string) => void;
  height?: number | string;
}

/** Status -> fill color + label, off the mnt palette. */
export function statusMeta(s: PlanMapStatus): { color: string; label: string } {
  switch (s) {
    case "open":
      return { color: mnt.amber, label: "Open" };
    case "in_production":
      // Aligned to the WO timeline's "live" color (mnt.link, blue) so
      // active work reads the same on the plan map and the work-order
      // timeline. Was mnt.teal.
      return { color: mnt.link, label: "In production" };
    case "closed":
      return { color: mnt.green, label: "Closed" };
    case "no_wo":
    default:
      return { color: mnt.grey, label: "No work order" };
  }
}

/** A teardrop pin SVG path (Google Maps Symbol). */
const PIN_PATH =
  "M 0,0 C -2.5,-6 -9,-8 -9,-15 A 9,9 0 1,1 9,-15 C 9,-8 2.5,-6 0,0 z";

export default function PlanLocationsMap(props: PlanLocationsMapProps) {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  if (loadError)
    return <div style={{ padding: 12, color: mnt.red }}>Error loading map</div>;
  if (!isLoaded)
    return <div style={{ padding: 12, color: mnt.textMuted }}>Loading map…</div>;
  return <TheMap {...props} />;
}

function TheMap({
  points,
  selectedId,
  onSelect,
  onOpenLocation,
  onOpenWorkOrder,
  height = 440,
}: PlanLocationsMapProps) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const boundsFittedRef = useRef(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [activeInfoId, setActiveInfoId] = useState<string | null>(null);

  // Only points with real coordinates can be plotted. The rest are
  // surfaced as a count so they're never silently dropped.
  const mappable = useMemo(
    () =>
      points.filter(
        (p) =>
          p.locationId &&
          typeof p.latitude === "number" &&
          typeof p.longitude === "number",
      ),
    [points],
  );
  const notMappable = points.length - mappable.length;

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    infoWindowRef.current = new google.maps.InfoWindow();
    setMapLoaded(true);
  }, []);

  const openInfoWindow = useCallback(
    (p: PlanMapPoint, position: google.maps.LatLng) => {
      const iw = infoWindowRef.current;
      const map = mapRef.current;
      if (!iw || !map || !p.locationId) return;

      const meta = statusMeta(p.mapStatus);
      const addr =
        p.address && typeof p.address === "object"
          ? [p.address.city, p.address.state].filter(Boolean).join(", ")
          : "";

      const container = document.createElement("div");
      container.style.cssText =
        "font-family:Poppins,sans-serif;font-size:12px;padding:2px 4px 0;min-width:190px;";

      const nameEl = document.createElement("div");
      nameEl.style.cssText = "font-weight:600;color:#000;font-size:13px;";
      nameEl.textContent = p.name || "Location";
      container.appendChild(nameEl);

      if (addr) {
        const addrEl = document.createElement("div");
        addrEl.style.cssText = "color:#5f5e5a;font-size:11px;margin:1px 0 8px;";
        addrEl.textContent = addr;
        container.appendChild(addrEl);
      }

      // Status line: a colored dot + label, plus the overdue flag.
      const statusEl = document.createElement("div");
      statusEl.style.cssText =
        "display:flex;align-items:center;gap:6px;font-size:11px;margin-bottom:4px;";
      const dot = document.createElement("span");
      dot.style.cssText = `width:9px;height:9px;border-radius:50%;background:${meta.color};display:inline-block;`;
      statusEl.appendChild(dot);
      const stTxt = document.createElement("span");
      stTxt.textContent = meta.label;
      statusEl.appendChild(stTxt);
      if (p.overdue) {
        const od = document.createElement("span");
        od.style.cssText = `color:${mnt.red};font-weight:600;margin-left:4px;`;
        od.textContent = "· Overdue";
        statusEl.appendChild(od);
      }
      container.appendChild(statusEl);

      // What's here, summarized.
      const summaryEl = document.createElement("div");
      summaryEl.style.cssText = "color:#5f5e5a;font-size:11px;margin-bottom:8px;";
      const woN = p.openWoCount ?? 0;
      summaryEl.textContent =
        woN > 0
          ? `${woN} open work order${woN === 1 ? "" : "s"}`
          : p.mapStatus === "closed"
          ? "No open work; last service complete"
          : "No work order raised yet";
      container.appendChild(summaryEl);

      if (p.batchName) {
        const batchEl = document.createElement("div");
        batchEl.style.cssText = "color:#888780;font-size:10px;margin-bottom:8px;";
        batchEl.textContent = p.batchName;
        container.appendChild(batchEl);
      }

      // Action buttons — a row so "View work order" (when this site has an
      // actionable WO) sits alongside "Open location". WO is the primary
      // action when present; location is always available.
      const hasWoAction =
        typeof onOpenWorkOrder === "function" && !!p.activeWorkOrderId;
      const hasLocAction = typeof onOpenLocation === "function";
      if (hasWoAction || hasLocAction) {
        const btnRow = document.createElement("div");
        btnRow.style.cssText = "display:flex;gap:6px;margin-top:2px;";

        if (hasWoAction) {
          const woBtn = document.createElement("button");
          woBtn.type = "button";
          woBtn.textContent = "View work order";
          woBtn.style.cssText =
            "background:#000;color:#fff;border:none;border-radius:4px;padding:4px 10px;font-size:11px;font-family:Poppins,sans-serif;cursor:pointer;";
          woBtn.onclick = () => {
            iw.close();
            setActiveInfoId(null);
            (onOpenWorkOrder as (id: string) => void)(
              p.activeWorkOrderId as string,
            );
          };
          btnRow.appendChild(woBtn);
        }

        if (hasLocAction) {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.textContent = "Open location";
          // When the WO button is present, the location button is the
          // secondary action, so it reads as an outline rather than solid.
          btn.style.cssText = hasWoAction
            ? "background:#fff;color:#000;border:1px solid #000;border-radius:4px;padding:4px 10px;font-size:11px;font-family:Poppins,sans-serif;cursor:pointer;"
            : "background:#000;color:#fff;border:none;border-radius:4px;padding:4px 10px;font-size:11px;font-family:Poppins,sans-serif;cursor:pointer;";
          btn.onclick = () => {
            iw.close();
            setActiveInfoId(null);
            (onOpenLocation as (id: string) => void)(p.locationId as string);
          };
          btnRow.appendChild(btn);
        }

        container.appendChild(btnRow);
      }

      iw.setContent(container);
      iw.setPosition(position);
      iw.open({ map });
      setActiveInfoId(p.locationId);
    },
    [onOpenLocation, onOpenWorkOrder],
  );

  // Reset the fitted flag when the mappable set size changes.
  useEffect(() => {
    boundsFittedRef.current = false;
  }, [mappable.length]);

  // (Re)render markers whenever the input changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    markersRef.current.forEach((mk) => mk.setMap(null));
    markersRef.current.clear();

    const bounds = new google.maps.LatLngBounds();
    let renderedAny = false;

    mappable.forEach((p) => {
      const id = p.locationId as string;
      const meta = statusMeta(p.mapStatus);
      const isSelected = selectedId === id;
      const isNoWo = p.mapStatus === "no_wo";

      const position = new google.maps.LatLng(
        p.latitude as number,
        p.longitude as number,
      );

      const marker = new google.maps.Marker({
        position,
        map,
        title: p.name || undefined,
        zIndex: isSelected ? 3 : p.overdue ? 2 : 1,
        icon: {
          path: PIN_PATH,
          // No-WO pins read as hollow: white fill, grey stroke. Others are
          // filled with the status color. Overdue thickens a red stroke.
          fillColor: isNoWo ? "#FFFFFF" : meta.color,
          fillOpacity: 1,
          strokeColor: p.overdue ? mnt.red : isNoWo ? mnt.grey : meta.color,
          strokeWeight: p.overdue ? 3 : isSelected ? 2.5 : 1,
          scale: isSelected ? 1.5 : 1.2,
          anchor: new google.maps.Point(0, 0),
        } as google.maps.Symbol,
      });

      marker.addListener("click", () => {
        if (typeof onSelect === "function") onSelect(id);
        openInfoWindow(p, position);
      });

      markersRef.current.set(id, marker);
      bounds.extend(position);
      renderedAny = true;
    });

    if (renderedAny && !boundsFittedRef.current) {
      map.fitBounds(bounds, 48);
      boundsFittedRef.current = true;
    }

    if (activeInfoId && !markersRef.current.has(activeInfoId)) {
      infoWindowRef.current?.close();
      setActiveInfoId(null);
    }
    // eslint-disable-next-line
  }, [mappable, mapLoaded, selectedId]);

  const fitAll = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const bounds = new google.maps.LatLngBounds();
    let any = false;
    markersRef.current.forEach((mk) => {
      const pos = mk.getPosition();
      if (pos) {
        bounds.extend(pos);
        any = true;
      }
    });
    if (any) map.fitBounds(bounds, 48);
    if (typeof onSelect === "function") onSelect(null);
  }, [onSelect]);

  const legendItems: PlanMapStatus[] = [
    "no_wo",
    "open",
    "in_production",
    "closed",
  ];

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <GoogleMap
        mapContainerStyle={{
          width: "100%",
          height: typeof height === "number" ? `${height}px` : height,
          borderRadius: "8px",
        }}
        zoom={4}
        center={DEFAULT_CENTER}
        onLoad={onLoad}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        }}
      />

      {/* Fit all */}
      <button
        type="button"
        onClick={fitAll}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          background: "#fff",
          border: `1px solid ${mnt.divider}`,
          borderRadius: 4,
          padding: "6px 12px",
          fontSize: 12,
          fontFamily: "Poppins, sans-serif",
          fontWeight: 500,
          cursor: "pointer",
          boxShadow: "0 2px 6px rgba(0,0,0,.2)",
        }}
      >
        Fit all
      </button>

      {/* Legend */}
      <div
        style={{
          position: "absolute",
          left: 10,
          bottom: 10,
          background: "#fff",
          border: `1px solid ${mnt.divider}`,
          borderRadius: 6,
          padding: "8px 10px",
          fontFamily: "Poppins, sans-serif",
          fontSize: 11,
          boxShadow: "0 2px 6px rgba(0,0,0,.15)",
        }}
      >
        {legendItems.map((s) => {
          const meta = statusMeta(s);
          const hollow = s === "no_wo";
          return (
            <div
              key={s}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: hollow ? "#fff" : meta.color,
                  border: hollow
                    ? `2px solid ${mnt.grey}`
                    : `1px solid ${meta.color}`,
                  display: "inline-block",
                }}
              />
              <span style={{ color: mnt.ink }}>{meta.label}</span>
            </div>
          );
        })}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            marginTop: 2,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "transparent",
              border: `2px solid ${mnt.red}`,
              display: "inline-block",
            }}
          />
          <span style={{ color: mnt.ink }}>Overdue (ring)</span>
        </div>
        {notMappable > 0 && (
          <div
            style={{
              marginTop: 6,
              paddingTop: 6,
              borderTop: `1px solid ${mnt.divider}`,
              color: mnt.textFaint,
            }}
          >
            {notMappable} not mappable
          </div>
        )}
      </div>
    </div>
  );
}
